import { currentLanguage, getNumberFormatter } from "./locale";

/**
 * Money, the one way this app writes it (rule 135, DS23, DS35). Ported from coach web's
 * `src/utilities/money.ts` so a figure means the same thing on both sides of a payment.
 *
 * Two forms and no third one:
 *
 * - a **row** (a fact row, a breakdown line, a total) reads `79,00 EUR` in French and
 *   `79.00 EUR` in English: the number in the reader's locale with its two decimals always,
 *   then the ISO code. Two decimals always, so a column of amounts lines up on its decimal
 *   mark; the ISO code and never a symbol, so USD, CAD and AUD cannot be confused.
 * - **running text and a button** read `79 €` in French and `€79` in English: the symbol where
 *   the language puts it, and no cents when there are none, because "Payer 79,00 EUR" reads
 *   like a ledger and "Payer 79 €" reads like a decision.
 *
 * Every caller passes UNITS, never cents. The API sends cents and `fromCents` is the one place
 * they are divided, at the boundary.
 */

/** Cents to units, once, at the edge of the app. Nothing downstream divides again. */
export function fromCents(cents: number): number {
  return cents / 100;
}

/** The amount as a row writes it: `79,00 EUR`, two decimals always, the ISO code, no symbol. */
export function formatTableMoney(
  value: number,
  currency: string,
  lang: string = currentLanguage(),
): string {
  const number = getNumberFormatter(lang, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  // A no-break space before the code, so an amount never wraps between its number and its unit.
  return `${number} ${currency.toUpperCase()}`;
}

/** The amount as a sentence or a button writes it: `79 €`, cents only when there are any. */
export function formatRunningMoney(
  value: number,
  currency: string,
  lang: string = currentLanguage(),
): string {
  const hasCents = Math.round(value * 100) % 100 !== 0;
  try {
    return getNumberFormatter(lang, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // An ISO code the runtime does not know throws in the constructor; the row form is the
    // honest fallback rather than a symbol invented for it.
    return formatTableMoney(value, currency, lang);
  }
}

/**
 * The amount split for the display block at the top of a Pay page (rule 130): the figure at 56
 * tabular and the ISO code at 20 beside it, so the eye lands on the number and reads the unit
 * after it. Never a symbol here, and never inside a coloured pill.
 */
export function splitDisplayMoney(
  value: number,
  currency: string,
  lang: string = currentLanguage(),
): { figure: string; unit: string } {
  return {
    figure: getNumberFormatter(lang, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value),
    unit: currency.toUpperCase(),
  };
}

/** The currency's own name, for the sentence that says whose money this is. */
export function currencyName(
  currency: string,
  lang: string = currentLanguage(),
): string {
  try {
    const names = new Intl.DisplayNames([lang], { type: "currency" });
    return names.of(currency.toUpperCase()) ?? currency.toUpperCase();
  } catch {
    return currency.toUpperCase();
  }
}
